import React, { useMemo } from 'react'
import { Checkbox, Form, InputNumber, Spin, Switch, Table } from 'antd'
import { useRequest } from 'ahooks'
import Button from '@/components/button/button'
import $msg from '@/utils/message'
import { Modal as SgModal } from '@/components/dialog/modal'
import Auth from '@/views/main/components/auth/auth'
import { layerManage, showLayer } from '@/utils/layer'
import { loadMemberSettingConfig, saveMemberSettingConfig } from '../api'
import SettingPage, {
    SettingActions,
    SettingGrid,
    SettingInputNumber,
    SettingPanel,
    SettingPreview,
    SettingTableContainer,
    numberOrUndefined,
} from './setting-page'
import {
    hasSvipChargeRuleAmountChanged,
    getSvipChargeRuleSyncOrder,
    SVIP_CHARGE_TYPE_MAP,
    validateSvipChargeRuleOrder,
    validateSvipMemberDiscount,
    validateSvipPointMultiplier,
} from './svip-setting-utils'
import { useMemberSettingAccess } from './setting-access'

interface SvipForm {
    status: boolean
    member_benefit_list: number[]
    discount_rate?: number
    point_multiplier?: number
    charge_rules: Array<{
        id: number
        charge_type: number
        charge?: number
        status: boolean
    }>
}

const benefitOptions = [
    { label: '商品会员价', value: 1 },
    { label: '会员折扣', value: 2 },
    { label: '多倍积分', value: 3 },
]
const BENEFIT_MEMBER_DISCOUNT = 2
const BENEFIT_POINT_BOOSTER = 3

const getHighestOrdinaryMemberDiscountRate = (activity?: any) => {
    const ordinaryLevels = (activity?.level_discount_list || [])
        .filter((item: any) => item.member_level && item.discount_rate !== null && item.discount_rate !== undefined)
        .sort((a: any, b: any) => Number(b.member_level?.level || 0) - Number(a.member_level?.level || 0))

    return ordinaryLevels.length ? Number(ordinaryLevels[0].discount_rate) : undefined
}

const getHighestOrdinaryPointMultiplier = (pointActivityInfo?: any) => {
    const ordinaryLevels = (pointActivityInfo?.point_booster_config?.levels || [])
        .filter(
            (item: any) =>
                !item.is_svip && item.member_level && item.multiplier !== null && item.multiplier !== undefined,
        )
        .sort((a: any, b: any) => Number(b.member_level?.level || 0) - Number(a.member_level?.level || 0))

    return ordinaryLevels.length ? Number(ordinaryLevels[0].multiplier) : undefined
}

const toChargeRuleForm = (item: any) => ({
    id: item.id,
    charge_type: item.charge_type,
    charge: Number(item.charge || 0),
    status: item.status === 0,
})

const SvipSetting: React.FC = () => {
    const [form] = Form.useForm<SvipForm>()
    const access = useMemberSettingAccess()
    const loadReq = useRequest(
        async () => {
            const [svipConfig, memberDiscountConfig, pointActivityInfo] = await Promise.all([
                loadMemberSettingConfig('/membership/getsvipconfigadmin'),
                loadMemberSettingConfig('/activity/getmemberdiscountactivity'),
                loadMemberSettingConfig('/activity/getpointactivityinfo'),
            ])

            return {
                ...svipConfig,
                member_discount_activity: memberDiscountConfig?.activity,
                point_activity_info: pointActivityInfo,
            }
        },
        {
            onSuccess: res => {
                const benefits = res?.benefit_list || {}
                const benefitList = Object.keys(benefits)
                    .map(key => benefits[key])
                    .filter((item: any) => item?.if_has)
                    .map((item: any) => item.benefit)
                form.setFieldsValue({
                    status: !!res?.status,
                    member_benefit_list: benefitList,
                    discount_rate: benefits.benefit_member_discount?.discount_rate,
                    point_multiplier: benefits.benefit_point_booster?.multiplier,
                    charge_rules: (res?.charge_rules || []).map(toChargeRuleForm),
                })
            },
            onError: e => $msg('error', e.message || '获取付费会员设置失败'),
        },
    )

    const syncChargeRules = async (rules: SvipForm['charge_rules']) => {
        const originRules = (loadReq.data?.charge_rules || []).map(toChargeRuleForm)
        const changedRules = getSvipChargeRuleSyncOrder(rules || [], originRules)
        for (const rule of changedRules) {
            await saveMemberSettingConfig('/membership/updatesvipchargerule', {
                id: rule.id,
                charge_type: rule.charge_type,
                charge: numberOrUndefined(rule.charge),
            })
        }

        const latestConfig = await loadMemberSettingConfig('/membership/getsvipconfigadmin')
        const latestRules = (latestConfig?.charge_rules || []).map(toChargeRuleForm)
        for (const rule of rules || []) {
            const latestRule = latestRules.find(
                (item: SvipForm['charge_rules'][number]) => item.charge_type === rule.charge_type,
            )
            if (latestRule && latestRule.status !== rule.status) {
                await saveMemberSettingConfig('/membership/editsvipchargerulestatus', {
                    id: latestRule.id,
                    charge_type: latestRule.charge_type,
                    status: rule.status ? 0 : 1,
                })
            }
        }
    }

    const saveReq = useRequest(
        async (values: SvipForm) => {
            const previousStatus = !!loadReq.data?.status
            if (values.status && !previousStatus) {
                await saveMemberSettingConfig('/membership/enablesvip', {})
            }

            if (values.status) {
                await saveMemberSettingConfig('/membership/updatesvipconfig', {
                    member_benefit_list: values.member_benefit_list || [],
                    discount_rate: numberOrUndefined(values.discount_rate),
                    point_multiplier: numberOrUndefined(values.point_multiplier),
                })
            }

            await syncChargeRules(values.charge_rules || [])

            if (!values.status && previousStatus) {
                await saveMemberSettingConfig('/membership/disablesvip', {})
            }
        },
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '保存成功')
                loadReq.refresh()
            },
            onError: e => $msg('error', e.message || '保存付费会员设置失败'),
        },
    )

    const columns = useMemo(
        () => [
            {
                title: '类型',
                dataIndex: 'charge_type',
                render: (value: number, __: any, index: number) => (
                    <>
                        <Form.Item name={['charge_rules', index, 'id']} hidden>
                            <InputNumber />
                        </Form.Item>
                        <Form.Item name={['charge_rules', index, 'charge_type']} hidden>
                            <InputNumber />
                        </Form.Item>
                        {SVIP_CHARGE_TYPE_MAP[value] || '未知'}
                    </>
                ),
            },
            {
                title: '费用',
                render: (_: any, __: any, index: number) => (
                    <Form.Item name={['charge_rules', index, 'charge']} noStyle>
                        <SettingInputNumber min={0} precision={2} addonAfter="元" />
                    </Form.Item>
                ),
            },
            {
                title: '状态',
                render: (_: any, __: any, index: number) => (
                    <Form.Item name={['charge_rules', index, 'status']} noStyle valuePropName="checked">
                        <Switch checkedChildren="启用" unCheckedChildren="停用" />
                    </Form.Item>
                ),
            },
        ],
        [],
    )

    const onSubmit = async () => {
        const values = await form.validateFields()
        const originRules = (loadReq.data?.charge_rules || []).map(toChargeRuleForm)
        const chargeOrderError = validateSvipChargeRuleOrder(values.charge_rules || [])
        if (
            (values.status || hasSvipChargeRuleAmountChanged(values.charge_rules || [], originRules)) &&
            chargeOrderError
        ) {
            $msg('warning', chargeOrderError)

            return
        }
        if (values.status) {
            if (!(values.member_benefit_list || []).length) {
                $msg('warning', '请至少选择一个付费会员权益')

                return
            }
            const memberDiscountError = validateSvipMemberDiscount(
                values.member_benefit_list || [],
                values.discount_rate,
                getHighestOrdinaryMemberDiscountRate(loadReq.data?.member_discount_activity),
            )
            if (memberDiscountError) {
                $msg('warning', memberDiscountError)

                return
            }
            const pointMultiplierError = validateSvipPointMultiplier(
                values.member_benefit_list || [],
                values.point_multiplier,
                getHighestOrdinaryPointMultiplier(loadReq.data?.point_activity_info),
            )
            if (pointMultiplierError) {
                $msg('warning', pointMultiplierError)

                return
            }

            const enabledRules = (values.charge_rules || []).filter(rule => rule.status)
            if (!enabledRules.length) {
                $msg('warning', '请至少启用一个付费会员收费标准')

                return
            }
            if (enabledRules.some(rule => !rule.charge || Number(rule.charge) <= 0)) {
                $msg('warning', '请填写已启用收费标准的费用')

                return
            }
        }
        if (loadReq.data?.status && !values.status) {
            showLayer(
                <SgModal
                    headerStyles={{ width: '440px' }}
                    isShow
                    type="danger"
                    style={{ height: '156px' }}
                    okText="确认停用"
                    onClose={() => layerManage.pop()}
                    onCancel={() => layerManage.pop()}
                    onConfirm={() => {
                        layerManage.pop()
                        saveReq.run(values)
                    }}
                >
                    <div>
                        <div data-font-size-20 data-font-weight-600 data-color-000>
                            确认停用付费会员？
                        </div>
                        <div data-font-size-14 data-color-999 data-margin-top-8>
                            停用后会清空付费会员权益，并移除付费会员相关折扣和多倍积分权益。
                        </div>
                    </div>
                </SgModal>,
            )

            return
        }

        saveReq.run(values)
    }

    return (
        <SettingPage title="付费会员" readOnly={access.readOnly}>
            <Spin spinning={loadReq.loading}>
                <Form form={form} layout="vertical">
                    <SettingGrid>
                        <SettingPanel title="权益配置" desc="配置付费会员可享受的核心权益。">
                            <Form.Item noStyle shouldUpdate>
                                {() => (
                                    <SettingPreview
                                        items={[
                                            {
                                                label: '付费会员',
                                                value: form.getFieldValue('status') ? '已开启' : '已关闭',
                                            },
                                            {
                                                label: '已选权益',
                                                value: `${(form.getFieldValue('member_benefit_list') || []).length} 项`,
                                            },
                                        ]}
                                    />
                                )}
                            </Form.Item>
                            <Form.Item name="status" label="付费会员状态" valuePropName="checked">
                                <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                            </Form.Item>
                            <Form.Item name="member_benefit_list" label="会员权益">
                                <Checkbox.Group options={benefitOptions} />
                            </Form.Item>
                            <Form.Item noStyle shouldUpdate>
                                {() =>
                                    (form.getFieldValue('member_benefit_list') || []).includes(
                                        BENEFIT_MEMBER_DISCOUNT,
                                    ) ? (
                                        <Form.Item name="discount_rate" label="付费会员折扣">
                                            <SettingInputNumber
                                                min={0.001}
                                                max={0.999}
                                                precision={3}
                                                addonAfter="折扣率"
                                            />
                                        </Form.Item>
                                    ) : null
                                }
                            </Form.Item>
                            <Form.Item noStyle shouldUpdate>
                                {() =>
                                    (form.getFieldValue('member_benefit_list') || []).includes(
                                        BENEFIT_POINT_BOOSTER,
                                    ) ? (
                                        <Form.Item name="point_multiplier" label="付费会员积分倍数">
                                            <SettingInputNumber min={1} max={10} precision={1} addonAfter="倍" />
                                        </Form.Item>
                                    ) : null
                                }
                            </Form.Item>
                        </SettingPanel>
                        <SettingPanel title="收费标准" desc="维护付费会员月卡、季卡、年卡的费用和启用状态。">
                            <Form.Item noStyle shouldUpdate>
                                {() => (
                                    <SettingTableContainer>
                                        <Table
                                            bordered
                                            rowKey="id"
                                            columns={columns}
                                            dataSource={form.getFieldValue('charge_rules') || []}
                                            pagination={false}
                                        />
                                    </SettingTableContainer>
                                )}
                            </Form.Item>
                            <SettingActions>
                                <Auth perssion={20} onClick={onSubmit}>
                                    <Button types="default" loading={saveReq.loading} disabled={saveReq.loading}>
                                        保存
                                    </Button>
                                </Auth>
                            </SettingActions>
                        </SettingPanel>
                    </SettingGrid>
                </Form>
            </Spin>
        </SettingPage>
    )
}

export default SvipSetting
